/**
 * 🛡️ G2GENTE - Minhas Candidaturas
 * Lista candidaturas com status: Inscrito, Em análise, Solicitação enviada, Aprovado, Recusado
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  FileText,
  Calendar,
  Building2,
  Send,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

export default function MobileApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await applicationsAPI.getMy();
      setApplications(response.data);
    } catch (error) {
      toast.error('Erro ao carregar candidaturas');
    } finally {
      setLoading(false);
    }
  };

  // Status configuration matching the requirement
  const getStatusConfig = (status) => {
    const configs = {
      PENDING: { 
        icon: Clock, 
        label: 'Inscrito', 
        color: 'bg-slate-100 text-slate-700',
        borderColor: 'border-slate-200',
        description: 'Sua candidatura foi recebida'
      },
      REVIEWING: { 
        icon: Eye, 
        label: 'Em Análise', 
        color: 'bg-blue-100 text-blue-700',
        borderColor: 'border-blue-200',
        description: 'Seu perfil está sendo analisado pela empresa'
      },
      SOLICITATION_SENT: { 
        icon: Send, 
        label: 'Solicitação Enviada', 
        color: 'bg-amber-100 text-amber-700',
        borderColor: 'border-amber-200',
        description: 'A empresa solicitou acesso aos seus dados'
      },
      ACCEPTED: { 
        icon: CheckCircle2, 
        label: 'Aprovado', 
        color: 'bg-emerald-100 text-emerald-700',
        borderColor: 'border-emerald-200',
        description: 'Parabéns! Você foi aprovado nesta etapa'
      },
      REJECTED: { 
        icon: XCircle, 
        label: 'Recusado', 
        color: 'bg-red-100 text-red-700',
        borderColor: 'border-red-200',
        description: 'Infelizmente não foi desta vez'
      },
      HIRED: { 
        icon: UserCheck, 
        label: 'Contratado', 
        color: 'bg-cyan-100 text-cyan-700',
        borderColor: 'border-cyan-200',
        description: 'Você foi contratado! Parabéns!'
      }
    };
    return configs[status] || configs.PENDING;
  };

  // Group applications by status for better organization
  const groupedApplications = applications.reduce((acc, app) => {
    const status = app.status || 'PENDING';
    if (!acc[status]) acc[status] = [];
    acc[status].push(app);
    return acc;
  }, {});

  const statusOrder = ['PENDING', 'REVIEWING', 'SOLICITATION_SENT', 'ACCEPTED', 'REJECTED', 'HIRED'];

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-applications">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-900">Minhas Candidaturas</h1>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Nenhuma candidatura</h3>
            <p className="text-sm text-slate-600 mb-4">
              Explore as vagas e candidate-se
            </p>
            <button 
              onClick={() => navigate('/m/jobs')}
              className="text-cyan-600 font-medium"
            >
              Ver Vagas →
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white rounded-xl p-3 text-center border border-slate-200">
                <p className="text-2xl font-bold text-slate-900">{applications.length}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-200">
                <p className="text-2xl font-bold text-emerald-600">
                  {applications.filter(a => a.status === 'ACCEPTED' || a.status === 'HIRED').length}
                </p>
                <p className="text-xs text-emerald-600">Aprovadas</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-200">
                <p className="text-2xl font-bold text-blue-600">
                  {applications.filter(a => a.status === 'REVIEWING' || a.status === 'SOLICITATION_SENT').length}
                </p>
                <p className="text-xs text-blue-600">Em Análise</p>
              </div>
            </div>

            {/* Applications List */}
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{applications.length} candidatura(s)</p>
              
              {applications.map((app) => {
                const statusConfig = getStatusConfig(app.status);
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card 
                    key={app.id} 
                    className={`border-l-4 ${statusConfig.borderColor}`}
                    data-testid={`mobile-application-${app.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 truncate">
                              {app.job_title || 'Vaga'}
                            </h3>
                            <Badge className={`${statusConfig.color} flex-shrink-0`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          
                          {app.company_name && (
                            <p className="text-sm text-slate-600 truncate">{app.company_name}</p>
                          )}
                          
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                            <Calendar className="w-3 h-3" />
                            Aplicado em {new Date(app.created_at).toLocaleDateString('pt-BR')}
                          </div>
                          
                          <p className="text-xs text-slate-500 mt-2 bg-slate-50 rounded p-2">
                            {statusConfig.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Legend */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-3 text-sm">Legenda de Status</h4>
              <div className="grid grid-cols-2 gap-2">
                {statusOrder.map(status => {
                  const config = getStatusConfig(status);
                  const Icon = config.icon;
                  return (
                    <div key={status} className="flex items-center gap-2 text-xs">
                      <Badge className={`${config.color} text-xs`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
